import { useTranslation } from "react-i18next";
import { FiArrowUpRight } from "react-icons/fi";
import CountUp from "@/components/reactbits/CountUp";
import { useGithubStats } from "@/hooks/useGithubStats";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { getSocialMediaLink } from "@/utils/external-links";
import WidgetShell from "./WidgetShell";

const LINK_CLASS =
    "inline-flex min-h-8 min-w-8 max-[770px]:min-h-11 max-[770px]:min-w-11 items-center justify-center rounded-md border border-subtle px-2.5 py-1 font-mono text-xs uppercase tracking-wider text-theme-light transition-colors hover:border-subtle-hover hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40 dark:focus-visible:ring-white/40";

const TILE_CLASS = "min-w-0 rounded-md border border-subtle px-3 py-4 max-[770px]:px-2";

const SKELETON_KEYS = ["commits", "pullRequests", "merged"] as const;

export default function GithubStats() {
    const { t, i18n } = useTranslation();
    const { stats, status } = useGithubStats();
    const reducedMotion = usePrefersReducedMotion();
    const profileUrl = getSocialMediaLink("github");

    const tiles = stats
        ? ([
              { key: "commits", value: stats.commits },
              { key: "pullRequests", value: stats.pullRequests },
              { key: "merged", value: stats.merged },
          ] as const)
        : [];

    return (
        <WidgetShell
            label={t("widgets.githubActivity")}
            actions={
                <a
                    href={profileUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={t("widgets.githubStats.viewProfile")}
                    title={t("widgets.githubStats.viewProfile")}
                    className={LINK_CLASS}
                >
                    <FiArrowUpRight aria-hidden="true" />
                </a>
            }
        >
            {status === "loading" && (
                <div
                    role="status"
                    aria-label={t("widgets.githubLoading")}
                    className="grid grid-cols-3 gap-3 max-[770px]:gap-2"
                >
                    {SKELETON_KEYS.map((key) => (
                        <div
                            key={key}
                            className="h-20 animate-pulse rounded-md bg-black/[0.06] dark:bg-white/10"
                        />
                    ))}
                </div>
            )}
            {status === "error" && (
                <div role="alert" className="w-full text-center">
                    <p className="text-sm text-theme-light opacity-80">{t("widgets.githubError")}</p>
                </div>
            )}
            {status === "ready" && stats && (
                <>
                    <div className="grid grid-cols-3 gap-3 max-[770px]:gap-2">
                        {tiles.map(({ key, value }) => (
                            <div key={key} className={TILE_CLASS} data-testid={`github-stat-${key}`}>
                                <div className="text-3xl font-semibold tabular-nums text-primary max-[770px]:text-2xl">
                                    {reducedMotion ? (
                                        value.toLocaleString(i18n.language)
                                    ) : (
                                        <>
                                            <span className="sr-only">{value.toLocaleString(i18n.language)}</span>
                                            <span aria-hidden="true">
                                                <CountUp to={value} duration={1.6} locale={i18n.language} />
                                            </span>
                                        </>
                                    )}
                                </div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-theme-light break-words">
                                    {t(`widgets.githubStats.${key}`)}
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-theme-light opacity-70">
                        {t("widgets.githubStats.window", { days: stats.days })}
                    </p>
                </>
            )}
        </WidgetShell>
    );
}
